import snakecaseKeys from 'snakecase-keys'
import axiosIns from '@/plugins/axios'

export default {
  // 顧客一覧を取得
  getCustomers({ q = null, page = 1, perPage = 50 } = {}) {
    return axiosIns.get('/waiter/customers', {
      params: snakecaseKeys({ q, page, perPage }),
    })
  },

  // 顧客一覧 + サマリー + 統計情報を取得（フィルター対応）
  getCustomersWithStats({
    startDate = null,
    endDate = null,
    q = null,
    customerTagIds = null,
    birthdayMonth = null,
    minVisitCount = null,
    visitCountStartDate = null,
    visitCountEndDate = null,
    lastVisitAfter = null,
    lastVisitBefore = null,
    rfmSegment = null,
    castIds = null,
    nominationRecipientIds = null,
    rootReferrersOnly = null,
  } = {}) {
    const params = { startDate, endDate }
    if (q) params.q = q
    if (customerTagIds && customerTagIds.length > 0) params.customerTagIds = customerTagIds
    if (birthdayMonth) params.birthdayMonth = birthdayMonth
    if (minVisitCount) params.minVisitCount = minVisitCount
    if (visitCountStartDate) params.visitCountStartDate = visitCountStartDate
    if (visitCountEndDate) params.visitCountEndDate = visitCountEndDate
    if (lastVisitAfter) params.lastVisitAfter = lastVisitAfter
    if (lastVisitBefore) params.lastVisitBefore = lastVisitBefore
    if (rfmSegment) params.rfmSegment = rfmSegment
    if (castIds && castIds.length > 0) params.castIds = castIds
    if (nominationRecipientIds && nominationRecipientIds.length > 0) params.nominationRecipientIds = nominationRecipientIds
    if (rootReferrersOnly) params.rootReferrersOnly = true

    return axiosIns.get('/waiter/customer_stats', {
      params: snakecaseKeys(params),
    })
  },

  // 単一顧客を取得
  getCustomer(id) {
    return axiosIns.get(`/waiter/customers/${id}`)
  },

  // 顧客詳細 + 購買履歴 + クロス集計を取得
  getCustomerStats(id, { startDate = null, endDate = null } = {}) {
    return axiosIns.get(`/waiter/customer_stats/${id}`, {
      params: snakecaseKeys({ startDate, endDate }),
    })
  },

  // 高単価顧客分析
  getHighValueAnalysis({
    startDate = null,
    endDate = null,
    totalThreshold = null,
    perVisitThreshold = null,
  }) {
    return axiosIns.get('/waiter/customer_stats/high_value_analysis', {
      params: snakecaseKeys({
        startDate,
        endDate,
        totalThreshold,
        perVisitThreshold,
      }),
    })
  },

  // 顧客ランキング
  getRanking({
    type = 'sales',
    limit = 10,
    startDate = null,
    endDate = null,
  } = {}) {
    return axiosIns.get('/waiter/customer_stats/ranking', {
      params: snakecaseKeys({
        type,
        limit,
        startDate,
        endDate,
      }),
    })
  },

  // 離反リスク顧客
  getChurnRisk({ thresholdDays = 30 } = {}) {
    return axiosIns.get('/waiter/customer_stats/churn_risk', {
      params: snakecaseKeys({ thresholdDays }),
    })
  },

  // RFM分析
  getRfmAnalysis({ startDate = null, endDate = null } = {}) {
    return axiosIns.get('/waiter/customer_stats/rfm_analysis', {
      params: snakecaseKeys({ startDate, endDate }),
    })
  },

  // 来店パターン分析（全体）
  getVisitPattern({ startDate = null, endDate = null } = {}) {
    return axiosIns.get('/waiter/customer_stats/visit_pattern', {
      params: snakecaseKeys({ startDate, endDate }),
    })
  },

  // 個別顧客の来店パターン分析
  getCustomerVisitPattern(id, { startDate = null, endDate = null } = {}) {
    return axiosIns.get(`/waiter/customers/${id}/customer_visit_pattern`, {
      params: snakecaseKeys({ startDate, endDate }),
    })
  },

  // 顧客統計の来店履歴を取得（ページネーション付き）
  getCustomerStatsVisitHistory(id, { page = 1, perPage = 10 } = {}) {
    return axiosIns.get(`/waiter/customer_stats/${id}/visit_history`, {
      params: snakecaseKeys({ page, perPage }),
    })
  },

  // 顧客の来店履歴を取得
  getVisitHistory(id, { page = 1, perPage = 10 } = {}) {
    return axiosIns.get(`/waiter/customers/${id}/visit_history`, {
      params: snakecaseKeys({ page, perPage }),
    })
  },

  // 顧客の購買履歴を取得
  getPurchaseHistory(id, { page = 1, perPage = 10 } = {}) {
    return axiosIns.get(`/waiter/customers/${id}/purchase_history`, {
      params: snakecaseKeys({ page, perPage }),
    })
  },

  // 顧客を作成
  createCustomer(customer) {
    return axiosIns.post('/waiter/customers', snakecaseKeys({ customer }))
  },

  // 顧客を更新
  updateCustomer(id, customer) {
    return axiosIns.put(`/waiter/customers/${id}`, snakecaseKeys({ customer }))
  },

  // 顧客を削除
  deleteCustomer(id) {
    return axiosIns.delete(`/waiter/customers/${id}`)
  },

  // 顧客のキャスト（関連キャスト）を取得
  getCustomerCasts(customerId) {
    return axiosIns.get(`/waiter/customers/${customerId}/casts`)
  },

  // 顧客のキャスト（関連キャスト）を更新
  updateCustomerCasts(customerId, castIds) {
    return axiosIns.put(`/waiter/customers/${customerId}/casts`, snakecaseKeys({ castIds }))
  },

  // ----- 顧客メモ関連 -----

  // 顧客メモ一覧を取得
  getCustomerNotes(customerId, {
    page = 1, perPage = 10, category = null, authorId = null,
  } = {}) {
    return axiosIns.get(`/waiter/customers/${customerId}/notes`, {
      params: snakecaseKeys({
        page, perPage, category, authorId,
      }),
    })
  },

  // 顧客メモを作成
  createCustomerNote(customerId, { content, category = null }) {
    return axiosIns.post(`/waiter/customers/${customerId}/notes`, snakecaseKeys({
      customerNote: { content, category },
    }))
  },

  // 顧客メモを更新
  updateCustomerNote(customerId, noteId, { content, category = null }) {
    return axiosIns.put(`/waiter/customers/${customerId}/notes/${noteId}`, snakecaseKeys({
      customerNote: { content, category },
    }))
  },

  // 顧客メモを削除
  deleteCustomerNote(customerId, noteId) {
    return axiosIns.delete(`/waiter/customers/${customerId}/notes/${noteId}`)
  },
}
